import type {
  TriedgeParityBridgeImplementation,
  TriedgeParityQueryState,
  TriedgeParityRouteReadiness,
} from '@triedge/bridge/global';
import { SOURCE_FAITHFUL_GRADE_ENTRY_QUERY } from '@triedge/academics-ui';
import { domainRouterUniversalApi, type DomainQueryRequest } from '../state/domainRouterUniversalApi';
import { ACTIVE_TUITION_ACADEMIC_YEAR } from '../internal-owned/tuition/shared/academicYear';
import type { RootState } from '../state/store';
import { store } from '../state/store';
import { resolveCognitoProfile } from '../utils/cognito-profile';

type RouteContext = {
  pathname: string;
  applicationId?: string;
  familyId?: string;
  studentId?: string;
  queryApplicationId?: string;
  parentApplicantId?: string;
};

type QueryPlan = {
  key: string;
  operation: DomainQueryRequest['operation'];
  resolveArgs: (context: RouteContext, state: RootState) => Record<string, unknown> | null;
  optional?: boolean;
  when?: (context: RouteContext, state: RootState) => boolean;
  allowErrorAsLoaded?: boolean;
};

type RoutePlan = {
  matches: (pathname: string) => boolean;
  queries: QueryPlan[];
};

const readPathname = (): string => (
  typeof window !== 'undefined' ? window.location.pathname : ''
);

const buildContext = (): RouteContext => {
  const pathname = readPathname();
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const admissionsMatch = pathname.match(
    /\/school\/admissions\/(?:children|family|documents|notes)\/([^/]+)$/,
  );
  const studentsMatch = pathname.match(
    /\/school\/students\/family\/([^/]+)\/(?:children|family|tuition|documents|notes)$/,
  );
  const financialAidMatch = pathname.match(
    /\/school\/financialaid\/staff\/([^/]+)\/(?:family-profile|income-calculation|assets-equity|expenses-spending|contract-flags|documents|notes)$/,
  );
  const attendanceStudentMatch = pathname.match(
    /\/school\/attendance\/students\/([^/]+)$/,
  );

  return {
    pathname,
    applicationId: admissionsMatch?.[1] || financialAidMatch?.[1],
    familyId: studentsMatch?.[1],
    studentId: attendanceStudentMatch?.[1],
    queryApplicationId: String(searchParams.get('applicationId') ?? '').trim() || undefined,
    parentApplicantId: resolveCognitoProfile()?.userSub || undefined,
  };
};

const selectDomainQuery = (
  state: RootState,
  request: DomainQueryRequest,
) => domainRouterUniversalApi.endpoints.domainQuery.select(request)(state);

const readRootState = (): RootState => store.getState() as RootState;

const toQueryState = (
  plan: QueryPlan,
  state: ReturnType<typeof selectDomainQuery>,
): TriedgeParityQueryState => ({
  key: plan.key,
  operation: plan.operation,
  status: state.status,
  isLoading: Boolean(('isLoading' in state ? state.isLoading : false) || state.status === 'pending'),
  isFetching: state.status === 'pending',
  isSuccess: Boolean(state.isSuccess),
  isError: Boolean(state.isError),
  hasData: state.data != null,
  error: state.error && typeof state.error === 'object' && 'message' in state.error
    ? String(state.error.message ?? '')
    : null,
});

const asRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
);

const currentIsoDate = (): string => new Date().toISOString().slice(0, 10);
const SETTINGS_DEFAULT_ORG_ID = '00000000-0000-4000-8000-000000000000';
const SETTINGS_MODULE_KEY = 'staff_settings';

const constantArgs = (args: Record<string, unknown> = {}) => () => args;

const admissionsDetailArgs = (context: RouteContext) => (
  context.applicationId ? { id: context.applicationId } : null
);

const parentAdmissionsArgs = (context: RouteContext) => (
  context.parentApplicantId ? { applicantId: context.parentApplicantId, limit: 25 } : null
);

const parentSelectedApplicationArgs = (context: RouteContext) => (
  context.queryApplicationId ? { id: context.queryApplicationId } : null
);

const studentsFamilyArgs = (context: RouteContext) => (
  context.familyId ? { familyId: context.familyId } : null
);

const financialAidDetailArgs = (context: RouteContext) => (
  context.applicationId ? { applicationId: context.applicationId } : null
);

const resolveAttendanceOverviewArgs = () => ({
  input: {
    schoolDate: currentIsoDate(),
  },
});

const resolveAttendanceCaptureContextArgs = () => ({
  input: {
    schoolDate: currentIsoDate(),
  },
});

const resolveAttendanceRecordsArgs = () => ({
  input: {
    schoolDate: currentIsoDate(),
    dayMode: 'day',
  },
});

const attendanceThirtySchoolDayWindowEndingAt = (schoolDate: string) => {
  const end = new Date(`${schoolDate}T12:00:00.000Z`);
  if (Number.isNaN(end.getTime())) {
    return { fromSchoolDate: schoolDate, toSchoolDate: schoolDate };
  }
  const dates: string[] = [];
  const cursor = new Date(end);
  while (dates.length < 30) {
    const day = cursor.getUTCDay();
    if (day >= 1 && day <= 5) {
      dates.push(cursor.toISOString().slice(0, 10));
    }
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }
  return {
    fromSchoolDate: dates[dates.length - 1] ?? schoolDate,
    toSchoolDate: dates[0] ?? schoolDate,
  };
};

const resolveAttendanceStudentHistoryArgs = (context: RouteContext) => (
  context.studentId
    ? (() => {
        const { fromSchoolDate, toSchoolDate } = attendanceThirtySchoolDayWindowEndingAt(currentIsoDate());
        return {
          input: {
            studentId: context.studentId,
            fromSchoolDate,
            toSchoolDate,
          },
        };
      })()
    : null
);

const resolveAttendanceAuditArgs = () => ({
  input: {
    limit: 25,
  },
});

const resolveAttendanceImportStatusArgs = () => ({
  input: {
    importId: 'import-demo-1',
  },
});

const PARENT_TUITION_MANAGEMENT_ARGS = {
  input: JSON.stringify({ moduleId: 'tuition' }),
};

const readParentTuitionFamilyId = (state: RootState): string | null => {
  const managementState = selectDomainQuery(state, {
    operation: 'getMyPaymentManagementContext',
    args: PARENT_TUITION_MANAGEMENT_ARGS,
  });
  const familyId = String(asRecord(managementState.data).familyId ?? '').trim();
  return familyId || null;
};

const resolveParentTuitionManagementArgs = () => PARENT_TUITION_MANAGEMENT_ARGS;

const resolveParentTuitionStudentProfileArgs = (
  _context: RouteContext,
  state: RootState,
): Record<string, unknown> | null => {
  const familyId = readParentTuitionFamilyId(state);
  return familyId
    ? { input: JSON.stringify({ familyId }) }
    : null;
};

const resolveParentTuitionBalanceArgs = (
  _context: RouteContext,
  state: RootState,
): Record<string, unknown> | null => {
  const familyId = readParentTuitionFamilyId(state);
  return familyId
    ? { familyId, academicYear: ACTIVE_TUITION_ACADEMIC_YEAR }
    : null;
};

const resolveParentTuitionPaymentMethodArgs = (
  _context: RouteContext,
  state: RootState,
): Record<string, unknown> | null => {
  const familyId = readParentTuitionFamilyId(state);
  return familyId ? { familyId } : null;
};

const resolveSchoolTuitionDashboardArgs = () => ({
  academicYear: ACTIVE_TUITION_ACADEMIC_YEAR,
  limit: 24,
  offset: 0,
});

const routePlans: Record<string, RoutePlan> = {
  'school-admissions-dashboard': {
    matches: (pathname) => pathname === '/school/admissions/dashboard',
    queries: [
      { key: 'applications', operation: 'listApplications', resolveArgs: constantArgs({ limit: 200 }) },
    ],
  },
  'school-admissions-children': {
    matches: (pathname) => /\/school\/admissions\/children\/[^/]+$/.test(pathname),
    queries: [
      { key: 'application', operation: 'getApplication', resolveArgs: admissionsDetailArgs },
    ],
  },
  'school-admissions-family': {
    matches: (pathname) => /\/school\/admissions\/family\/[^/]+$/.test(pathname),
    queries: [
      { key: 'application', operation: 'getApplication', resolveArgs: admissionsDetailArgs },
    ],
  },
  'school-admissions-documents': {
    matches: (pathname) => /\/school\/admissions\/documents\/[^/]+$/.test(pathname),
    queries: [
      { key: 'application', operation: 'getApplication', resolveArgs: admissionsDetailArgs },
      {
        key: 'documents',
        operation: 'getApplicationDocuments',
        resolveArgs: (context) => (
          context.applicationId ? { applicationId: context.applicationId, limit: 100 } : null
        ),
      },
    ],
  },
  'school-admissions-notes': {
    matches: (pathname) => /\/school\/admissions\/notes\/[^/]+$/.test(pathname),
    queries: [
      { key: 'application', operation: 'getApplication', resolveArgs: admissionsDetailArgs },
      {
        key: 'timeline',
        operation: 'listTimelineEvents',
        resolveArgs: (context) => (
          context.applicationId ? { applicationId: context.applicationId, limit: 100 } : null
        ),
      },
    ],
  },
  'parent-admissions-applications': {
    matches: (pathname) => pathname === '/parent/portal/applications',
    queries: [
      {
        key: 'applications',
        operation: 'getApplicantApplications',
        resolveArgs: parentAdmissionsArgs,
      },
    ],
  },
  'parent-admissions-intake': {
    matches: (pathname) => pathname === '/parent/portal--new/applications',
    queries: [
      {
        key: 'selected-application',
        operation: 'getApplication',
        resolveArgs: parentSelectedApplicationArgs,
        when: (context) => Boolean(context.queryApplicationId),
        allowErrorAsLoaded: true,
      },
      {
        key: 'applications',
        operation: 'getApplicantApplications',
        resolveArgs: parentAdmissionsArgs,
        when: (context) => !context.queryApplicationId,
      },
    ],
  },
  'school-settings-general': {
    matches: (pathname) => pathname === '/school/settings/general',
    queries: [
      { key: 'module-config', operation: 'getModuleConfig', resolveArgs: constantArgs({ moduleKey: 'staff_settings' }) },
      { key: 'payment-readiness', operation: 'getPaymentControlPlaneReadiness', resolveArgs: constantArgs({}) },
      { key: 'audit-events', operation: 'listModuleEnablementAuditEvents', resolveArgs: constantArgs({ limit: 5 }) },
    ],
  },
  'school-settings-users': {
    matches: (pathname) => pathname === '/school/settings/users',
    queries: [
      { key: 'module-config', operation: 'getModuleConfig', resolveArgs: constantArgs({ moduleKey: SETTINGS_MODULE_KEY }) },
      {
        key: 'staff-users',
        operation: 'listStaffUsers',
        resolveArgs: constantArgs({ statusFilter: 'all', page: 1, limit: 20 }),
      },
    ],
  },
  'school-settings-admissions': {
    matches: (pathname) => pathname === '/school/settings/admissions',
    queries: [
      { key: 'module-config', operation: 'getModuleConfig', resolveArgs: constantArgs({ moduleKey: 'staff_settings' }) },
    ],
  },
  'school-settings-financial-aid': {
    matches: (pathname) => pathname === '/school/settings/financialaid',
    queries: [
      { key: 'module-config', operation: 'getModuleConfig', resolveArgs: constantArgs({ moduleKey: SETTINGS_MODULE_KEY }) },
      {
        key: 'historical-import-status',
        operation: 'getFinancialAidHistoricalImportStatus',
        resolveArgs: constantArgs({
          orgId: SETTINGS_DEFAULT_ORG_ID,
          moduleKey: SETTINGS_MODULE_KEY,
          importId: 'latest',
        }),
      },
    ],
  },
  'control-plane-integrations': {
    matches: (pathname) => pathname === '/super-administrator/control-plane/integrations',
    queries: [
      { key: 'payment-readiness', operation: 'getPaymentControlPlaneReadiness', resolveArgs: constantArgs({}) },
      { key: 'assistant-bridge-status', operation: 'getAssistantSpreadsheetBridgeStatus', resolveArgs: constantArgs({}) },
      { key: 'ui-consultation-status', operation: 'getUiConsultationServiceStatus', resolveArgs: constantArgs({}) },
    ],
  },
  'school-students-dashboard': {
    matches: (pathname) => pathname === '/school/students/dashboard',
    queries: [
      { key: 'families', operation: 'listFamilies', resolveArgs: constantArgs({ input: JSON.stringify({ limit: 200 }) }) },
    ],
  },
  'school-students-children': {
    matches: (pathname) => /\/school\/students\/family\/[^/]+\/children$/.test(pathname),
    queries: [
      { key: 'family', operation: 'getFamily', resolveArgs: studentsFamilyArgs },
    ],
  },
  'school-students-family': {
    matches: (pathname) => /\/school\/students\/family\/[^/]+\/family$/.test(pathname),
    queries: [
      { key: 'family', operation: 'getFamily', resolveArgs: studentsFamilyArgs },
      {
        key: 'communications',
        operation: 'listCommunications',
        resolveArgs: (context) => (context.familyId ? { familyId: context.familyId, limit: 100 } : null),
      },
    ],
  },
  'school-students-tuition': {
    matches: (pathname) => /\/school\/students\/family\/[^/]+\/tuition$/.test(pathname),
    queries: [
      { key: 'family', operation: 'getFamily', resolveArgs: studentsFamilyArgs },
      {
        key: 'balance',
        operation: 'getFamilyBalance',
        resolveArgs: (context) => (context.familyId ? { familyId: context.familyId, academicYear: '2026-2027' } : null),
      },
      {
        key: 'payment-methods',
        operation: 'listFamilyPaymentMethods',
        resolveArgs: (context) => (context.familyId ? { familyId: context.familyId, limit: 100 } : null),
      },
      {
        key: 'transactions',
        operation: 'listFamilyAccountTransactions',
        resolveArgs: (context) => (context.familyId ? { familyId: context.familyId, limit: 100 } : null),
      },
    ],
  },
  'school-students-documents': {
    matches: (pathname) => /\/school\/students\/family\/[^/]+\/documents$/.test(pathname),
    queries: [
      { key: 'family', operation: 'getFamily', resolveArgs: studentsFamilyArgs },
      {
        key: 'documents',
        operation: 'listFamilyDocuments',
        resolveArgs: (context) => (context.familyId ? { familyId: context.familyId, limit: 200 } : null),
      },
    ],
  },
  'school-students-notes': {
    matches: (pathname) => /\/school\/students\/family\/[^/]+\/notes$/.test(pathname),
    queries: [
      { key: 'family', operation: 'getFamily', resolveArgs: studentsFamilyArgs },
      {
        key: 'timeline',
        operation: 'listFamilyTimelineEvents',
        resolveArgs: (context) => (context.familyId ? { familyId: context.familyId, limit: 100 } : null),
      },
    ],
  },
  'school-financial-aid-dashboard': {
    matches: (pathname) => pathname === '/school/financialaid/dashboard',
    queries: [
      {
        key: 'review-items',
        operation: 'listFinancialAidApplicationsForReview',
        resolveArgs: constantArgs({ input: JSON.stringify({ limit: 100 }) }),
      },
    ],
  },
  'school-financial-aid-application': {
    matches: (pathname) => /\/school\/financialaid\/staff\/[^/]+\/family-profile$/.test(pathname),
    queries: [
      {
        key: 'detail',
        operation: 'getFinancialAidApplicationForReview',
        resolveArgs: financialAidDetailArgs,
      },
    ],
  },
  'school-financial-aid-income-calculation': {
    matches: (pathname) => /\/school\/financialaid\/staff\/[^/]+\/income-calculation$/.test(pathname),
    queries: [
      { key: 'detail', operation: 'getFinancialAidApplicationForReview', resolveArgs: financialAidDetailArgs },
    ],
  },
  'school-financial-aid-assets-equity': {
    matches: (pathname) => /\/school\/financialaid\/staff\/[^/]+\/assets-equity$/.test(pathname),
    queries: [
      { key: 'detail', operation: 'getFinancialAidApplicationForReview', resolveArgs: financialAidDetailArgs },
    ],
  },
  'school-financial-aid-expenses-spending': {
    matches: (pathname) => /\/school\/financialaid\/staff\/[^/]+\/expenses-spending$/.test(pathname),
    queries: [
      { key: 'detail', operation: 'getFinancialAidApplicationForReview', resolveArgs: financialAidDetailArgs },
    ],
  },
  'school-financial-aid-contract-flags': {
    matches: (pathname) => /\/school\/financialaid\/staff\/[^/]+\/contract-flags$/.test(pathname),
    queries: [
      { key: 'detail', operation: 'getFinancialAidApplicationForReview', resolveArgs: financialAidDetailArgs },
    ],
  },
  'school-financial-aid-documents': {
    matches: (pathname) => /\/school\/financialaid\/staff\/[^/]+\/documents$/.test(pathname),
    queries: [
      { key: 'detail', operation: 'getFinancialAidApplicationForReview', resolveArgs: financialAidDetailArgs },
    ],
  },
  'school-financial-aid-notes': {
    matches: (pathname) => /\/school\/financialaid\/staff\/[^/]+\/notes$/.test(pathname),
    queries: [
      { key: 'detail', operation: 'getFinancialAidApplicationForReview', resolveArgs: financialAidDetailArgs },
    ],
  },
  'school-payroll-dashboard': {
    matches: (pathname) => pathname === '/school/payroll',
    queries: [
      { key: 'dashboard', operation: 'getPayrollDashboard', resolveArgs: constantArgs({}) },
    ],
  },
  'school-faculty-directory': {
    matches: (pathname) => pathname === '/school/faculty' || pathname === '/school/faculty/directory',
    queries: [
      { key: 'faculty', operation: 'listFaculty', resolveArgs: constantArgs({ filter: {} }) },
    ],
  },
  'school-attendance-overview': {
    matches: (pathname) => pathname === '/school/attendance',
    queries: [
      { key: 'capture-context', operation: 'getAttendanceCaptureContext', resolveArgs: resolveAttendanceCaptureContextArgs },
      { key: 'overview', operation: 'getAttendanceOverview', resolveArgs: resolveAttendanceOverviewArgs },
    ],
  },
  'school-attendance-new-records': {
    matches: (pathname) => pathname === '/school/attendance/new-records',
    queries: [
      { key: 'capture-context', operation: 'getAttendanceCaptureContext', resolveArgs: resolveAttendanceCaptureContextArgs },
      { key: 'records', operation: 'listAttendanceRecords', resolveArgs: resolveAttendanceRecordsArgs },
    ],
  },
  'school-attendance-teacher-view': {
    matches: (pathname) => pathname === '/school/attendance/teacher-view',
    queries: [
      { key: 'capture-context', operation: 'getAttendanceCaptureContext', resolveArgs: resolveAttendanceCaptureContextArgs },
      { key: 'records', operation: 'listAttendanceRecords', resolveArgs: resolveAttendanceRecordsArgs },
    ],
  },
  'school-attendance-student-history': {
    matches: (pathname) => pathname.startsWith('/school/attendance/students/'),
    queries: [
      { key: 'capture-context', operation: 'getAttendanceCaptureContext', resolveArgs: resolveAttendanceCaptureContextArgs },
      { key: 'student-history', operation: 'getAttendanceStudentHistory', resolveArgs: resolveAttendanceStudentHistoryArgs },
    ],
  },
  'super-admin-attendance-control-plane': {
    matches: (pathname) => pathname === '/super-administrator/control-plane/attendance',
    queries: [
      { key: 'config', operation: 'getAttendanceControlPlaneConfig', resolveArgs: constantArgs({ input: {} }) },
      { key: 'audit-events', operation: 'listAttendanceAuditEvents', resolveArgs: resolveAttendanceAuditArgs, optional: true },
      { key: 'import-status', operation: 'getAttendanceImportStatus', resolveArgs: resolveAttendanceImportStatusArgs, optional: true },
    ],
  },
  'school-substitutes-root': {
    matches: (pathname) => pathname === '/school/substitutes' || pathname === '/school/substitutes/assignments',
    queries: [
      { key: 'dashboard', operation: 'getSubstituteDashboard', resolveArgs: constantArgs({}) },
      { key: 'absences', operation: 'listOpenAbsences', resolveArgs: constantArgs({}) },
      { key: 'pool', operation: 'listSubstitutePool', resolveArgs: constantArgs({}) },
      { key: 'schedule', operation: 'listSubstituteSchedule', resolveArgs: constantArgs({}) },
      { key: 'payroll', operation: 'getSubstitutePayroll', resolveArgs: constantArgs({}) },
    ],
  },
  'school-tuition-root': {
    matches: (pathname) => pathname === '/school/tuition',
    queries: [
      { key: 'dashboard', operation: 'getSchoolTuitionDashboardTree', resolveArgs: resolveSchoolTuitionDashboardArgs },
    ],
  },
  'administrator-tuition-root': {
    matches: (pathname) => pathname === '/administrator/tuition',
    queries: [
      { key: 'dashboard', operation: 'getSchoolTuitionDashboardTree', resolveArgs: resolveSchoolTuitionDashboardArgs },
      { key: 'payment-readiness', operation: 'getPaymentControlPlaneReadiness', resolveArgs: constantArgs({}) },
    ],
  },
  'parent-tuition-root': {
    matches: (pathname) => pathname === '/parent/portal/tuition',
    queries: [
      {
        key: 'management-context',
        operation: 'getMyPaymentManagementContext',
        resolveArgs: resolveParentTuitionManagementArgs,
      },
      {
        key: 'student-profiles',
        operation: 'listPortalStudentProfiles',
        resolveArgs: resolveParentTuitionStudentProfileArgs,
        optional: true,
      },
      {
        key: 'balance',
        operation: 'getFamilyBalance',
        resolveArgs: resolveParentTuitionBalanceArgs,
        optional: true,
      },
      {
        key: 'transactions',
        operation: 'listFamilyAccountTransactions',
        resolveArgs: resolveParentTuitionBalanceArgs,
        optional: true,
      },
      {
        key: 'payment-methods',
        operation: 'listFamilyPaymentMethods',
        resolveArgs: resolveParentTuitionPaymentMethodArgs,
        optional: true,
      },
    ],
  },
  'school-donations-root': {
    matches: (pathname) => pathname === '/school/donations',
    queries: [
      { key: 'campaigns', operation: 'getDonationsCampaignList', resolveArgs: constantArgs({}) },
      { key: 'ledger', operation: 'getDonationsLedger', resolveArgs: constantArgs({}) },
      { key: 'pending', operation: 'listDonationsPendingDedications', resolveArgs: constantArgs({}) },
    ],
  },
  'school-tip-a-teacher-root': {
    matches: (pathname) => pathname === '/school/tip-a-teacher',
    queries: [
      { key: 'overview', operation: 'getTipSchoolOverview', resolveArgs: constantArgs({}) },
      { key: 'program-settings', operation: 'getTipProgramSettings', resolveArgs: constantArgs({}) },
      { key: 'notifications', operation: 'listTipNotifications', resolveArgs: constantArgs({}) },
    ],
  },
  'school-camps-root': {
    matches: (pathname) => pathname === '/school/camps',
    queries: [
      { key: 'dashboard', operation: 'getSchoolDashboard', resolveArgs: constantArgs({}) },
    ],
  },
  'school-academics-root': {
    matches: (pathname) => pathname === '/school/academics',
    queries: [
      { key: 'workspace', operation: 'getGradeEntryWorkspace', resolveArgs: constantArgs(SOURCE_FAITHFUL_GRADE_ENTRY_QUERY) },
    ],
  },
  'school-finance-root': {
    matches: (pathname) => pathname === '/school/finance',
    queries: [
      { key: 'tuition-snapshot', operation: 'getSchoolTuitionDashboardTree', resolveArgs: resolveSchoolTuitionDashboardArgs },
      { key: 'donations-ledger', operation: 'getDonationsLedger', resolveArgs: constantArgs({}) },
      { key: 'payment-mirror', operation: 'listPaymentMirrorLedgerEntries', resolveArgs: constantArgs({}) },
    ],
  },
};

const createReadiness = (
  caseId: string,
  status: TriedgeParityRouteReadiness['status'],
  pathname: string,
  reason: string,
  queries: TriedgeParityQueryState[] = [],
): TriedgeParityRouteReadiness => ({
  caseId,
  status,
  pathname,
  reason,
  queries,
  inspectedAt: new Date().toISOString(),
});

const currentPathIsAuthRedirect = (pathname: string): boolean =>
  pathname.startsWith('/auth') || pathname.startsWith('/apply');

const summarizeQueries = (
  state: RootState,
  context: RouteContext,
  plan: RoutePlan,
): { reason?: string; queries: Array<{ plan: QueryPlan; state: TriedgeParityQueryState }> } => {
  const queries: Array<{ plan: QueryPlan; state: TriedgeParityQueryState }> = [];

  for (const queryPlan of plan.queries) {
    if (queryPlan.when && !queryPlan.when(context, state)) {
      continue;
    }
    const args = queryPlan.resolveArgs(context, state);
    if (!args) {
      if (queryPlan.optional) {
        continue;
      }
      return { reason: `Unable to resolve arguments for ${queryPlan.key}.`, queries };
    }
    queries.push({
      plan: queryPlan,
      state: toQueryState(
        queryPlan,
        selectDomainQuery(state, {
          operation: queryPlan.operation,
          args,
        }),
      ),
    });
  }

  return { queries };
};

const primeRoute = async (caseId: string): Promise<void> => {
  const context = buildContext();
  const plan = routePlans[caseId];
  if (!plan || currentPathIsAuthRedirect(context.pathname) || !plan.matches(context.pathname)) {
    return;
  }

  const state = readRootState();
  for (const queryPlan of plan.queries) {
    if (queryPlan.when && !queryPlan.when(context, state)) {
      continue;
    }
    const args = queryPlan.resolveArgs(context, state);
    if (!args) {
      continue;
    }
    store.dispatch(
      domainRouterUniversalApi.endpoints.domainQuery.initiate(
        {
          operation: queryPlan.operation,
          args,
        },
        { subscribe: false },
      ),
    );
  }
};

const getRouteReadiness = (caseId: string): TriedgeParityRouteReadiness => {
  const context = buildContext();
  const plan = routePlans[caseId];

  if (currentPathIsAuthRedirect(context.pathname)) {
    return createReadiness(caseId, 'auth_redirect', context.pathname, `Current route redirected to ${context.pathname}.`);
  }
  if (!plan) {
    return createReadiness(caseId, 'unsupported', context.pathname, 'No parity route plan is registered.');
  }
  if (!plan.matches(context.pathname)) {
    return createReadiness(caseId, 'unmapped', context.pathname, `Current path '${context.pathname}' does not match case '${caseId}'.`);
  }

  const summary = summarizeQueries(readRootState(), context, plan);
  if (summary.reason) {
    return createReadiness(caseId, 'error', context.pathname, summary.reason, summary.queries.map((entry) => entry.state));
  }
  const queryStates = summary.queries.map((entry) => entry.state);
  if (summary.queries.some((entry) => entry.state.isError && !entry.plan.allowErrorAsLoaded)) {
    return createReadiness(caseId, 'error', context.pathname, 'One or more parity RTK queries failed.', queryStates);
  }
  if (summary.queries.some((entry) => !entry.state.isSuccess && !(entry.state.isError && entry.plan.allowErrorAsLoaded))) {
    return createReadiness(caseId, 'loading', context.pathname, 'Parity RTK queries are still loading.', queryStates);
  }

  return {
    caseId,
    status: 'loaded',
    pathname: context.pathname,
    reason: null,
    queries: queryStates,
    inspectedAt: new Date().toISOString(),
  };
};

export const createYstnjParityBridge = (): TriedgeParityBridgeImplementation => ({
  primeRoute,
  getRouteReadiness,
});
